/* =========================================================
   Reports — data model: the column catalogue, the filter
   shape, and the row builders for each category.
   ========================================================= */
import type { ReactNode } from 'react';
import type { Lang } from '@/types';
import { ic, type IconElement } from '@/components/Icons';
import { COUNTRIES, ds } from '@/data';
import { instructorSchema, orgSchema, siteSchema } from '@/lib/fields';
import { progresoFicha } from '@/lib/progreso';
import { CATALOGOS, SEP_LISTA, aLista, type EntidadNA } from '../../../db/columns';
import { fechaHoraOrdenable } from '@/lib/actualizado';

export type Category = 'orgs' | 'sites' | 'instructors' | 'consolidated';

export const CATS: Category[] = ['orgs', 'sites', 'instructors', 'consolidated'];

export const L = (lang: Lang, es: string, en: string) => (lang === 'es' ? es : en);

export function countryName(code: string | undefined, lang: Lang) {
  const c = COUNTRIES.find((x) => x.code === code);
  return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
}

export const fullName = (a?: string, b?: string) => [a, b].filter(Boolean).join(' ');

export interface Comp {
  pct: number;
  missing: number;
  pending: boolean;
}

/** Rows carry the source record plus the derived `_*` helpers. */
export type ReportRow = Record<string, unknown> & { _key: string };

export interface ColDef {
  key: string;
  es: string;
  en: string;
  get: (r: ReportRow, lang: Lang) => string | number | undefined;
  cell?: (r: ReportRow, lang: Lang) => ReactNode;
  mono?: boolean;
  align?: 'left' | 'right' | 'center';
  /** Shown by default. */
  def?: boolean;
}

/* ---------- completeness per category ---------- */

/** El mismo cálculo que enseña el portal, para que el informe no diga otra
 *  cosa que la pantalla.
 *
 *  Antes cada categoría se medía a su manera: los sites por sus tres campos
 *  rojos, y las organizaciones e instructores por una lista escogida a mano
 *  de seis y cinco campos, porque sus esquemas no tienen ninguno obligatorio.
 *  Eran tres reglas distintas y ninguna coincidía con el porcentaje del
 *  proceso de actualización, así que el Excel y el panel discrepaban.
 *
 *  Aquí es la ficha suelta, no el conjunto: el informe tiene una fila por
 *  registro, así que el compliance de la organización y los instructores del
 *  site no entran — esos viven en el número agregado del panel. */
const FICHA: Partial<Record<Category, { entidad: EntidadNA; clave: string }>> = {
  orgs: { entidad: 'organization', clave: 'OrgId' },
  sites: { entidad: 'site', clave: 'SiteId' },
  instructors: { entidad: 'instructor', clave: 'InstructorId' },
};

export function recordCompleteness(category: Category, row: Record<string, unknown>): Comp {
  // el cruzado no es una ficha, es una relación: no tiene porcentaje propio
  const f = FICHA[category];
  if (!f) return { pct: 100, missing: 0, pending: false };
  const { entidad, clave } = f;
  const id = String(row[clave] ?? '');
  const p = progresoFicha(entidad, id, row, ds.marcasNA());
  const faltan = p.total - p.hechos;
  return { pct: p.pct, missing: faltan, pending: faltan > 0 };
}

/* ---------- consolidated cross-tab ---------- */

/** One row per Org → Site → Instructor relation. Organizations with no
 *  sites and sites with no instructors still appear, so gaps are visible. */
export function buildConsolidated(): ReportRow[] {
  const out: ReportRow[] = [];
  let n = 0;
  const px = (obj: Record<string, unknown>, prefix: string) => {
    const o: Record<string, unknown> = {};
    for (const k in obj) o[prefix + k] = obj[k];
    return o;
  };

  for (const org of ds.listOrgs()) {
    const orgBase = px(org as unknown as Record<string, unknown>, 'org_');
    const sites = ds.listSites({ orgId: org.OrgId });
    if (sites.length === 0) {
      out.push({ _key: 'cx' + n++, ...orgBase });
      continue;
    }
    for (const site of sites) {
      const base = { ...orgBase, ...px(site as unknown as Record<string, unknown>, 'site_') };
      const asgs = ds.listAssignments({ siteId: site.SiteId });
      if (asgs.length === 0) {
        out.push({ _key: 'cx' + n++, ...base });
        continue;
      }
      for (const a of asgs) {
        const i = ds.getInstructor(a.InstructorId) ?? {};
        out.push({
          _key: 'cx' + n++,
          ...base,
          ...px(i as unknown as Record<string, unknown>, 'instr_'),
          PrimaryRoleId: a.PrimaryRoleId,
        });
      }
    }
  }
  return out;
}

/* ---------- column catalogue ---------- */

const s = (v: unknown) => (v == null ? '' : String(v));

/** Los códigos de un campo con catálogo, traducidos a etiquetas.
 *
 *  Separados por el mismo `;` que espera la importación, para que el informe
 *  se pueda volver a subir tal cual. */
const etiquetas = (campo: string, valor: unknown, l: Lang): string => {
  const cat = CATALOGOS[campo] ?? [];
  return aLista(valor)
    .map((c) => {
      const o = cat.find((x) => x.code === c);
      return o ? (l === 'es' ? o.label_es : o.label_en) : c;
    })
    .join(SEP_LISTA + ' ');
};

const orgCols: ColDef[] = [
  { key: 'OrgId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.OrgId) },
  { key: 'OrgName', es: 'Organización', en: 'Organization', def: true, get: (r) => s(r.OrgName) },
  { key: 'OrgCountry', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.OrgCountry), l) },
  { key: 'RegisteredCity', es: 'Ciudad', en: 'City', get: (r) => s(r.RegisteredCity) },
  { key: 'YearJoined', es: 'Año ingreso', en: 'Year joined', mono: true, align: 'center', def: true, get: (r) => s(r.YearJoined) },
  { key: '_nSites', es: 'Sites', en: 'Sites', mono: true, align: 'center', def: true, get: (r) => Number(r._nSites) },
  { key: '_comp', es: 'Completitud', en: 'Completeness', def: true, align: 'left', get: (r) => (r._comp as Comp).pct + '%' },
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
  { key: 'OrgWebAddress', es: 'Sitio web', en: 'Website', get: (r) => s(r.OrgWebAddress) },
  { key: '_legal', es: 'Contacto legal', en: 'Legal contact', get: (r) => fullName(s(r.LegalContactFirstName), s(r.LegalContactLastName)) },
  { key: 'LegalContactEmailAddress', es: 'Email legal', en: 'Legal email', get: (r) => s(r.LegalContactEmailAddress) },
  { key: 'BillingContactEmailAddress', es: 'Email facturación', en: 'Billing email', get: (r) => s(r.BillingContactEmailAddress) },
  { key: 'ContractCity', es: 'Ciudad contractual', en: 'Contract city', get: (r) => s(r.ContractCity) },

  /* Perfil del socio. Salen por defecto: son lo que el proceso de
     actualización existe para recoger, y un informe que no los enseñe no
     sirve para saber quién ha contestado.

     Se exportan con la ETIQUETA, no con el código. El informe lo lee una
     persona, y «aeco» no le dice nada. La importación acepta las dos formas,
     así que un archivo exportado se puede volver a subir sin tocarlo. */
  { key: 'EmployeeCount', es: 'Empleados', en: 'Employees', def: true, align: 'center', get: (r, l) => etiquetas('EmployeeCount', r.EmployeeCount, l) },
  { key: 'OrgIndustry', es: 'Industria', en: 'Industry', def: true, get: (r, l) => etiquetas('OrgIndustry', r.OrgIndustry, l) },
  { key: 'ProductPortfolio', es: 'Portafolio', en: 'Portfolio', def: true, get: (r, l) => etiquetas('ProductPortfolio', r.ProductPortfolio, l) },
  { key: 'ProductPortfolioOther', es: 'Portafolio · otro', en: 'Portfolio · other', get: (r) => s(r.ProductPortfolioOther) },
  { key: 'TrainingEnvironment', es: 'Entorno de formación', en: 'Training environment', def: true, get: (r, l) => etiquetas('TrainingEnvironment', r.TrainingEnvironment, l) },
  { key: 'OffersCertPrep', es: 'Prepara certificaciones', en: 'Delivers cert. prep', def: true, align: 'center', get: (r, l) => etiquetas('OffersCertPrep', r.OffersCertPrep, l) },
  { key: 'OffersEduTraining', es: 'Capacitación EDU', en: 'EDU training', def: true, align: 'center', get: (r, l) => etiquetas('OffersEduTraining', r.OffersEduTraining, l) },
  { key: 'InterestedEduTraining', es: 'Interés en sector EDU', en: 'Interest in EDU sector', def: true, align: 'center', get: (r, l) => etiquetas('InterestedEduTraining', r.InterestedEduTraining, l) },
];

const siteCols: ColDef[] = [
  { key: 'SiteId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.SiteId) },
  { key: 'SiteName', es: 'Site', en: 'Site', def: true, get: (r) => s(r.SiteName) },
  { key: 'OrgName', es: 'Organización', en: 'Organization', def: true, get: (r) => s(r.OrgName) },
  { key: 'OrgId', es: 'ID org.', en: 'Org ID', mono: true, get: (r) => s(r.OrgId) },
  { key: 'SiteCountry', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.SiteCountry), l) },
  { key: 'SiteCity', es: 'Ciudad', en: 'City', get: (r) => s(r.SiteCity) },
  { key: 'ATCCSN', es: 'ATCCSN', en: 'ATCCSN', mono: true, get: (r) => s(r.ATCCSN) },
  { key: 'Workstations', es: 'Estaciones', en: 'Workstations', mono: true, align: 'center', def: true, get: (r) => s(r.Workstations) },
  { key: '_manager', es: 'Site Manager', en: 'Site Manager', def: true, get: (r) => fullName(s(r.SiteManagerFirstName), s(r.SiteManagerLastName)) },
  { key: '_director', es: 'ATC Director', en: 'ATC Director', get: (r) => fullName(s(r.ATCDirectorFirstName), s(r.ATCDirectorLastName)) },
  { key: '_primary', es: 'Primario', en: 'Primary', def: true, align: 'center', get: (r, l) => (r._primary ? L(l, 'Sí', 'Yes') : 'No') },
  { key: 'SitePostalCode', es: 'C.P.', en: 'Postal', mono: true, get: (r) => s(r.SitePostalCode) },
  { key: '_comp', es: 'Completitud', en: 'Completeness', def: true, get: (r) => (r._comp as Comp).pct + '%' },
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
];

const instrCols: ColDef[] = [
  { key: 'InstructorId', es: 'ID', en: 'ID', mono: true, def: true, get: (r) => s(r.InstructorId) },
  { key: '_name', es: 'Instructor', en: 'Instructor', def: true, get: (r) => fullName(s(r.EnglishFirstName), s(r.EnglishLastName)) },
  { key: 'JobTitle', es: 'Cargo', en: 'Title', def: true, get: (r) => s(r.JobTitle) },
  { key: 'EmailAddress', es: 'Email', en: 'Email', def: true, get: (r) => s(r.EmailAddress) },
  { key: 'Mobile1', es: 'Móvil', en: 'Mobile', mono: true, get: (r) => s(r.Mobile1) },
  { key: 'Country', es: 'País', en: 'Country', def: true, get: (r, l) => countryName(s(r.Country), l) },
  { key: 'Certification', es: 'Certificación', en: 'Certification', def: true, get: (r) => s(r.Certification) },
  { key: 'AccessRoles', es: 'Roles de acceso', en: 'Access roles', get: (r) => s(r.AccessRoles) },
  { key: '_nAsg', es: 'Asignaciones', en: 'Assignments', mono: true, align: 'center', def: true, get: (r) => Number(r._nAsg) },
  { key: '_status', es: 'Estado', en: 'Status', def: true, align: 'left', get: (r, l) => (r._status === 'disassociated' ? L(l, 'Desasociado', 'Disassociated') : L(l, 'Activo', 'Active')) },
  { key: 'YearJoined', es: 'Año ingreso', en: 'Year joined', mono: true, align: 'center', get: (r) => s(r.YearJoined) },
  { key: 'LastUpdated', es: 'Última actualización', en: 'Last updated', mono: true, def: true, get: (r) => fechaHoraOrdenable(r as { LastUpdated?: string; LastUpdatedAt?: string }) },
];

/** The consolidated report exposes EVERY field of Organizations, Sites and
 *  Instructors in one cross-tab; the column picker decides what is shown. */
function buildConsolidatedCols(): ColDef[] {
  const mk = (prefix: string, schema: typeof orgSchema, group: string, defs: string[]): ColDef[] =>
    schema.map((fld) => ({
      key: prefix + fld.key,
      es: group + ' · ' + fld.label_es,
      en: group + ' · ' + fld.label_en,
      mono: fld.kind === 'mono',
      def: defs.includes(fld.key),
      get:
        fld.kind === 'country'
          ? (r: ReportRow, l: Lang) => countryName(s(r[prefix + fld.key]), l)
          : (r: ReportRow) => s(r[prefix + fld.key]),
    }));

  const oc = mk('org_', orgSchema, 'Org.', ['OrgId', 'OrgName', 'OrgCountry']);
  // org identifiers repeat on the site record — drop them so nothing duplicates
  const siteDrop = ['site_OrgId', 'site_OrgName', 'site_OrgWebAddress', 'site_PrimarySiteId'];
  const sc = mk('site_', siteSchema, 'Site', ['SiteId', 'SiteName', 'SiteCity']).filter((c) => !siteDrop.includes(c.key));
  const role: ColDef = { key: 'PrimaryRoleId', es: 'Rol en el site', en: 'Role at site', def: true, get: (r) => s(r.PrimaryRoleId) };
  const iname: ColDef = {
    key: '_iname',
    es: 'Instructor (nombre)',
    en: 'Instructor (name)',
    def: true,
    get: (r) => fullName(s(r.instr_EnglishFirstName), s(r.instr_EnglishLastName)),
  };
  const icols = mk('instr_', instructorSchema, 'Instr.', ['InstructorId']);
  return [...oc, ...sc, role, ...icols.slice(0, 1), iname, ...icols.slice(1)];
}

export const COLS: Record<Category, ColDef[]> = {
  orgs: orgCols,
  sites: siteCols,
  instructors: instrCols,
  consolidated: buildConsolidatedCols(),
};

export const CAT_META: Record<Category, { es: string; en: string; icon: IconElement; sheet: string }> = {
  orgs: { es: 'Organizaciones', en: 'Organizations', icon: ic.org, sheet: 'Organizaciones' },
  sites: { es: 'Sites', en: 'Sites', icon: ic.site, sheet: 'Sites' },
  instructors: { es: 'Instructores', en: 'Instructors', icon: ic.instr, sheet: 'Instructores' },
  consolidated: { es: 'Consolidado', en: 'Consolidated', icon: ic.table, sheet: 'Consolidado' },
};

/* ---------- filters ---------- */

export interface Filters {
  q: string;
  country: string;
  yearMin: string;
  yearMax: string;
  updatedFrom: string;
  updatedTo: string;
  completeness: 'all' | 'complete' | 'pending';
  status: string;
  certification: string;
  hasManager: 'all' | 'yes' | 'no';
  primary: 'all' | 'yes' | 'no';
  multiSite: 'all' | 'multi' | 'single';
}

export function defaultFilters(): Filters {
  return {
    q: '', country: '', yearMin: '', yearMax: '', updatedFrom: '', updatedTo: '',
    completeness: 'all', status: 'all', certification: '', hasManager: 'all', primary: 'all', multiSite: 'all',
  };
}

export type ColSelection = Record<Category, string[]>;

export function defaultCols(): ColSelection {
  const out = {} as ColSelection;
  CATS.forEach((c) => {
    out[c] = COLS[c].filter((x) => x.def).map((x) => x.key);
  });
  return out;
}

export function applyFilters(category: Category, f: Filters): ReportRow[] {
  if (category === 'consolidated') {
    const q = f.q.trim().toLowerCase();
    return buildConsolidated().filter((r) => {
      if (
        q &&
        ![r.org_OrgId, r.org_OrgName, r.site_SiteId, r.site_SiteName, r.instr_InstructorId,
          fullName(s(r.instr_EnglishFirstName), s(r.instr_EnglishLastName)), r.instr_EmailAddress]
          .some((v) => v && String(v).toLowerCase().includes(q))
      ) return false;
      if (f.country && r.org_OrgCountry !== f.country && r.site_SiteCountry !== f.country) return false;
      if (f.yearMin && r.org_YearJoined && +s(r.org_YearJoined) < +f.yearMin) return false;
      if (f.yearMax && r.org_YearJoined && +s(r.org_YearJoined) > +f.yearMax) return false;
      if (f.updatedFrom && r.org_LastUpdated && s(r.org_LastUpdated) < f.updatedFrom) return false;
      if (f.updatedTo && r.org_LastUpdated && s(r.org_LastUpdated) > f.updatedTo) return false;
      if (f.status === 'with' && !r.instr_InstructorId) return false;
      if (f.status === 'without' && r.instr_InstructorId) return false;
      return true;
    });
  }

  const list: Record<string, unknown>[] =
    category === 'orgs' ? (ds.listOrgs({ q: f.q }) as unknown as Record<string, unknown>[])
    : category === 'sites' ? (ds.listSites({ q: f.q }) as unknown as Record<string, unknown>[])
    : (ds.listInstructors({ q: f.q }) as unknown as Record<string, unknown>[]);

  const out: ReportRow[] = [];
  for (const row of list) {
    const cc = category === 'orgs' ? row.OrgCountry : category === 'sites' ? row.SiteCountry : row.Country;
    if (f.country && cc !== f.country) continue;
    const yr = s(row.YearJoined);
    if (f.yearMin && yr && +yr < +f.yearMin) continue;
    if (f.yearMax && yr && +yr > +f.yearMax) continue;
    if (f.updatedFrom && row.LastUpdated && s(row.LastUpdated) < f.updatedFrom) continue;
    if (f.updatedTo && row.LastUpdated && s(row.LastUpdated) > f.updatedTo) continue;

    const comp = recordCompleteness(category, row);
    if (f.completeness === 'complete' && comp.pending) continue;
    if (f.completeness === 'pending' && !comp.pending) continue;

    if (category === 'orgs' && f.multiSite !== 'all') {
      const n = ds.listSites({ orgId: s(row.OrgId) }).length;
      if (f.multiSite === 'multi' && n <= 1) continue;
      if (f.multiSite === 'single' && n > 1) continue;
    }
    if (category === 'sites') {
      const hasMgr = !!(row.SiteManagerFirstName && row.SiteManagerLastName);
      if (f.hasManager === 'yes' && !hasMgr) continue;
      if (f.hasManager === 'no' && hasMgr) continue;
      const isPrimary = row.SiteId === row.PrimarySiteId;
      if (f.primary === 'yes' && !isPrimary) continue;
      if (f.primary === 'no' && isPrimary) continue;
    }
    if (category === 'instructors') {
      const status = row.disassociated ? 'disassociated' : 'active';
      if (f.status !== 'all' && f.status !== status) continue;
      if (f.certification && row.Certification !== f.certification) continue;
    }

    // key on the record's OWN id — site and instructor rows also carry an
    // OrgId, which would collide across every site of the same organization
    const idKey = category === 'orgs' ? 'OrgId' : category === 'sites' ? 'SiteId' : 'InstructorId';
    const d: ReportRow = { ...row, _comp: comp, _key: s(row[idKey]) };
    if (category === 'orgs') d._nSites = ds.listSites({ orgId: s(row.OrgId) }).length;
    if (category === 'sites') {
      d._primary = row.SiteId === row.PrimarySiteId;
      d._manager = !!(row.SiteManagerFirstName && row.SiteManagerLastName);
    }
    if (category === 'instructors') {
      d._nAsg = ds.listAssignments({ instructorId: s(row.InstructorId) }).length;
      d._status = row.disassociated ? 'disassociated' : 'active';
    }
    out.push(d);
  }
  return out;
}
